import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';

const milestones = [
  { year: '2008', title: 'Company Founded', desc: 'Yunxin Outdoor Products was established in Nan\'an City, Fujian Province, with a vision to create world-class tool bags.' },
  { year: '2012', title: 'First Export Order', desc: 'Secured our first major international export contract, marking the beginning of our global expansion journey.' },
  { year: '2016', title: 'Factory Expansion', desc: 'Expanded our manufacturing facility to 15,000 sqm, doubling production capacity to meet growing global demand.' },
  { year: '2019', title: 'ISO 9001 Certified', desc: 'Achieved ISO 9001:2015 certification, affirming our commitment to international quality management standards.' },
  { year: '2022', title: '50+ Countries', desc: 'Our products now reach professionals in over 50 countries across 6 continents, establishing Yunxin as a global brand.' },
  { year: '2026', title: 'Innovation Lab', desc: 'Launched our dedicated R&D Innovation Lab to develop next-generation materials and smart storage solutions.' },
];

const stats = [
  { value: '15+', label: 'Years of Excellence' },
  { value: '200+', label: 'Product Models' },
  { value: '50+', label: 'Countries Served' },
  { value: '500+', label: 'Global Partners' },
];

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  '@id': `${SITE_URL}/about`,
  url: `${SITE_URL}/about`,
  name: 'About Us | YUNXIN OUTDOOR',
  description: 'Learn about Quanzhou Yunxin Outdoor Products Co., Ltd. — a world-class manufacturer of premium tool bags and organizer bags with 15+ years of excellence.',
  mainEntity: {
    '@type': 'Organization',
    name: 'Quanzhou Yunxin Outdoor Products Co., Ltd.',
    foundingDate: '2008',
    numberOfEmployees: { '@type': 'QuantitativeValue', value: 300 },
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'No.6 Sunshine Road, Xiamei Town',
      addressLocality: "Nan'an City",
      addressRegion: 'Fujian Province',
      addressCountry: 'CN',
    },
    hasCredential: {
      '@type': 'EducationalOccupationalCredential',
      name: 'ISO 9001:2015',
    },
  },
};

export default function AboutPage() {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-about';
    script.text = JSON.stringify(aboutSchema);
    document.head.appendChild(script);
    document.title = 'About Us | YUNXIN OUTDOOR — Premium Bag Manufacturer';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'Learn about Quanzhou Yunxin Outdoor Products Co., Ltd. — a world-class manufacturer of premium tool bags and organizer bags with 15+ years of excellence in Fujian, China.');
    return () => {
      const el = document.getElementById('schema-about');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=aerial%20view%20modern%20manufacturing%20factory%20building%20exterior%20Fujian%20China%20industrial%20park%20professional%20photography&width=1920&height=700&seq=about-hero&orientation=landscape"
          alt="Yunxin Facility"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/30" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
          <span className="text-amber-400 text-xs font-semibold tracking-widest uppercase mb-4 block">About Us</span>
          <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight mb-6 max-w-3xl">
            Quanzhou Yunxin<br />Outdoor Products
          </h1>
          <p className="text-white/70 text-lg max-w-xl leading-relaxed">
            A world-class manufacturer of premium tool bags and organizer bags, rooted in Fujian&apos;s rich manufacturing heritage.
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-stone-900 py-14">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-amber-400 mb-2">{s.value}</div>
                <div className="text-stone-400 text-sm tracking-wider uppercase">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-amber-600 text-xs font-semibold tracking-widest uppercase mb-4 block">Our Story</span>
              <h2 className="text-4xl md:text-5xl font-bold text-stone-900 leading-tight mb-6">
                Passion for Quality,<br />Driven by Innovation
              </h2>
              <p className="text-stone-500 text-base leading-relaxed mb-5">
                Founded in 2008, Quanzhou Yunxin Outdoor Products Co., Ltd. began as a small workshop with a big dream: to create tool bags that professionals could truly rely on. Today, we operate a 15,000 sqm state-of-the-art manufacturing facility in Nan&apos;an City, Fujian Province.
              </p>
              <p className="text-stone-500 text-base leading-relaxed mb-5">
                Our team of over 300 skilled craftspeople combines traditional sewing expertise with modern manufacturing technology to produce bags that meet the highest international standards. Every product is a testament to our unwavering commitment to quality.
              </p>
              <p className="text-stone-500 text-base leading-relaxed mb-8">
                From electricians in Europe to contractors in North America, our bags are trusted by professionals who demand the best. We take pride in being the silent partner that helps them do their best work every day.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-stone-900 hover:bg-stone-700 text-white px-8 py-4 rounded-full text-sm font-semibold tracking-widest uppercase transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Partner With Us
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line" />
                </div>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-2xl overflow-hidden h-64">
                <img
                  src="https://readdy.ai/api/search-image?query=workers%20sewing%20bags%20in%20modern%20factory%20production%20line%20quality%20manufacturing%20Fujian%20China&width=400&height=500&seq=about1&orientation=portrait"
                  alt="Manufacturing"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="rounded-2xl overflow-hidden h-64 mt-8">
                <img
                  src="https://readdy.ai/api/search-image?query=quality%20control%20inspection%20premium%20bags%20factory%20worker%20checking%20stitching%20detail%20close%20up&width=400&height=500&seq=about2&orientation=portrait"
                  alt="Quality Control"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="rounded-2xl overflow-hidden h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=fabric%20material%20warehouse%20rolls%20of%20nylon%20canvas%20organized%20storage%20manufacturing%20facility&width=400&height=300&seq=about3&orientation=landscape"
                  alt="Materials"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="rounded-2xl overflow-hidden h-48 mt-4">
                <img
                  src="https://readdy.ai/api/search-image?query=finished%20premium%20tool%20bags%20packaged%20ready%20for%20export%20shipping%20warehouse%20organized&width=400&height=300&seq=about4&orientation=landscape"
                  alt="Export Ready"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Milestones */}
      <section className="py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-16">
            <span className="text-amber-600 text-xs font-semibold tracking-widest uppercase mb-3 block">Our Journey</span>
            <h2 className="text-4xl md:text-5xl font-bold text-stone-900">Key Milestones</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {milestones.map((m) => (
              <div key={m.year} className="bg-white rounded-2xl p-8">
                <div className="text-4xl font-bold text-amber-600 mb-3">{m.year}</div>
                <h3 className="text-stone-900 font-semibold text-lg mb-3">{m.title}</h3>
                <p className="text-stone-500 text-sm leading-relaxed">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-16 bg-stone-900">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="flex gap-4 items-start">
              <div className="w-10 h-10 flex items-center justify-center bg-amber-700 rounded-xl flex-shrink-0">
                <i className="ri-map-pin-line text-white" />
              </div>
              <div>
                <p className="text-stone-400 text-xs tracking-wider uppercase mb-1">Address</p>
                <p className="text-white text-sm leading-relaxed">No.6 Sunshine Road, Xiamei Town, Nan&apos;an City, Fujian Province, China</p>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <div className="w-10 h-10 flex items-center justify-center bg-amber-700 rounded-xl flex-shrink-0">
                <i className="ri-phone-line text-white" />
              </div>
              <div>
                <p className="text-stone-400 text-xs tracking-wider uppercase mb-1">Phone</p>
                <a href="tel:+8613790296151" rel="nofollow" className="text-white text-sm hover:text-amber-400 transition-colors">+86 137 9029 6151</a>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <div className="w-10 h-10 flex items-center justify-center bg-amber-700 rounded-xl flex-shrink-0">
                <i className="ri-mail-line text-white" />
              </div>
              <div>
                <p className="text-stone-400 text-xs tracking-wider uppercase mb-1">Email</p>
                <a href="mailto:andy-zou@qzyunxin.com" rel="nofollow" className="text-white text-sm hover:text-amber-400 transition-colors block">andy-zou@qzyunxin.com</a>
                <a href="mailto:375974361@qq.com" rel="nofollow" className="text-white text-sm hover:text-amber-400 transition-colors block mt-1">375974361@qq.com</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
